import { SidebarProvider } from "@/components/ui/sidebar";
import { LearningSidebar } from "./LearningSidebar";
import { AssessmentSidebar } from "./AssessmentSidebar";
import { AppHeader } from "./AppHeader";
import { MobileBottomNav } from "./MobileBottomNav";
import { OfflineIndicator } from "./OfflineIndicator";
import { SidebarSkeleton, HeaderSkeleton, MainContentSkeleton } from "./SkeletonLoader";
import { ActiveLocationProvider } from "@/contexts/ActiveLocationContext";
import { CenterSwitcherDialog } from "./CenterSwitcherDialog";
import { useAutoLogout } from "@/hooks/use-auto-logout";
import { useRole } from "@/contexts/RoleContext";
import { usePathway } from "@/contexts/PathwayContext";
import { Suspense, useState, useCallback } from "react";
import { SpotlightTour } from "./spotlight-tour/SpotlightTour";
import { PageTour } from "./spotlight-tour/PageTour";
import { NavShellLayout } from "./NavShell/NavShellLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileTutorShell } from "./mobile-tutor/MobileTutorShell";
import { Capacitor } from "@capacitor/core";
import { AccountSetupIncomplete } from "./AccountSetupIncomplete";


export function AppLayout({ children }: { children: React.ReactNode }) {
  useAutoLogout();
  const { isLoading, userRole, roleResolution } = useRole();
  const { currentPathway } = usePathway();
  const isMobile = useIsMobile();
  const isNative = Capacitor.isNativePlatform();
  
  // Sidebar always defaults to expanded
  const [defaultOpen, setDefaultOpen] = useState<boolean>(true);
  const [onboardingActive, setOnboardingActive] = useState(false);

  const handleOnboardingActiveChange = useCallback((active: boolean) => {
    setOnboardingActive(active);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    localStorage.setItem("sidebar-state", open ? "open" : "closed");
    setDefaultOpen(open);
  }, []);

  if (isNative) {
    return (
      <ActiveLocationProvider>
        <Suspense fallback={<MainContentSkeleton />}>
          {children}
        </Suspense>
      </ActiveLocationProvider>
    );
  }

  // If role is still loading, check localStorage for a cached role to avoid full-page skeleton
  const cachedRole = localStorage.getItem("lurneax_active_role");
  const NAV_SHELL_ROLES = new Set(["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner", "franchisee_prospect", "parent", "student"]);
  const showNavShellWhileLoading =
    roleResolution === 'loading' && cachedRole !== null && NAV_SHELL_ROLES.has(cachedRole);

  // LOADING state with no cached role → neutral full-page shell.
  // The legacy sidebar MUST NOT render while resolution is in flight.
  if (roleResolution === 'loading' && !showNavShellWhileLoading) {
    return (
      <div className="min-h-screen flex w-full">
        <SidebarSkeleton />
        <div className="flex-1 flex flex-col">
          <HeaderSkeleton />
          <main className="flex-1 bg-background">
            <MainContentSkeleton />
          </main>
        </div>
      </div>
    );
  }

  // RESOLVED_NULL — account has no user_roles row. Surface loudly rather
  // than silently degrading to the legacy sidebar.
  if (roleResolution === 'no_role') {
    return <AccountSetupIncomplete />;
  }

  // Mobile tutor shell — completely replaces the layout
  if (roleResolution === 'resolved' && userRole === "tutor" && isMobile) {
    return (
      <ActiveLocationProvider>
        <MobileTutorShell />
        <CenterSwitcherDialog />
      </ActiveLocationProvider>
    );
  }

  // Use NavShell layout for master_admin and admin
  const useNavShell = userRole ? NAV_SHELL_ROLES.has(userRole) : false;

  if (useNavShell || showNavShellWhileLoading) {
    return (
      <ActiveLocationProvider>
        <SidebarProvider
          open={defaultOpen}
          onOpenChange={handleOpenChange}
        >
          <NavShellLayout>
            <Suspense fallback={<MainContentSkeleton />}>
              {isLoading ? <MainContentSkeleton /> : children}
            </Suspense>
          </NavShellLayout>
          {!isLoading && <SpotlightTour onActiveChange={handleOnboardingActiveChange} />}
          {!isLoading && <PageTour onboardingActive={onboardingActive} />}
          <CenterSwitcherDialog />
        </SidebarProvider>
      </ActiveLocationProvider>
    );
  }

  // Select sidebar based on current pathway
  const SidebarComponent = currentPathway === "learning" ? LearningSidebar : AssessmentSidebar;

  return (
    <ActiveLocationProvider>
      <SidebarProvider 
        open={defaultOpen}
        onOpenChange={handleOpenChange}
      >
        <div className="min-h-screen flex w-full">
          <SidebarComponent />
          <div className="flex-1 flex flex-col transition-all duration-300 ease-in-out">
            <div data-tour="header" className="h-14 flex items-center border-b border-border/40 px-2 sm:px-4 bg-background sticky top-0 z-50">
              <div className="flex-1 overflow-hidden">
                <AppHeader />
              </div>
            </div>
            <OfflineIndicator />
            <main className="flex-1 p-3 sm:p-4 lg:p-6 bg-background pb-20 md:pb-6 transition-all duration-300">
              <Suspense fallback={<MainContentSkeleton />}>
                {children}
              </Suspense>
            </main>
          </div>
          <MobileBottomNav />
          <SpotlightTour onActiveChange={handleOnboardingActiveChange} />
          <PageTour onboardingActive={onboardingActive} />
          <CenterSwitcherDialog />
        </div>
      </SidebarProvider>
    </ActiveLocationProvider>
  );
}
